// Server-Sent Events par fetch (navigateur et Node) : l'en-tête
// Authorization passe, la reconnexion reprend après coupure ou expiration.

export interface SseMessage {
  event: string
  data: string
}

/** Découpe un flux texte en messages SSE ; garde le reste incomplet. */
export function parseSse(buffer: string): { messages: SseMessage[]; rest: string; retry?: number } {
  const messages: SseMessage[] = []
  let retry: number | undefined
  const blocks = buffer.replace(/\r\n/g, '\n').split('\n\n')
  const rest = blocks.pop() ?? ''
  for (const block of blocks) {
    let event = 'message'
    const data: string[] = []
    for (const line of block.split('\n')) {
      if (!line || line.startsWith(':')) continue
      const i = line.indexOf(':')
      const field = i < 0 ? line : line.slice(0, i)
      const value = i < 0 ? '' : line.slice(i + 1).replace(/^ /, '')
      if (field === 'event') event = value
      else if (field === 'data') data.push(value)
      else if (field === 'retry' && /^\d+$/.test(value)) retry = Number(value)
    }
    if (data.length) messages.push({ event, data: data.join('\n') })
  }
  return { messages, rest, retry }
}

export interface SseOptions {
  /** Ouvre la connexion (en-têtes frais à chaque reconnexion). */
  open: (signal: AbortSignal) => Promise<Response>
  onMessage: (m: SseMessage) => void
  onOpen?: () => void
  /** Le flux vient de tomber (il sera rouvert). */
  onDown?: () => void
  /** Délai de reconnexion initial (doublé jusqu'à 30 s). */
  retryMs?: number
}

/** Lance le flux ; la fonction rendue l'arrête définitivement. */
export function streamSse(opts: SseOptions): () => void {
  const ctrl = new AbortController()
  let delay = opts.retryMs ?? 3000
  const loop = async () => {
    while (!ctrl.signal.aborted) {
      try {
        const res = await opts.open(ctrl.signal)
        if (!res.ok || !res.body) throw new Error(`flux ${res.status}`)
        opts.onOpen?.()
        delay = opts.retryMs ?? 3000
        const reader = res.body.getReader()
        const decoder = new TextDecoder()
        let buffer = ''
        for (;;) {
          const { value, done } = await reader.read()
          if (done) break
          buffer += decoder.decode(value, { stream: true })
          const parsed = parseSse(buffer)
          buffer = parsed.rest
          if (parsed.retry) delay = parsed.retry
          parsed.messages.forEach(opts.onMessage)
        }
      } catch {
        // coupure, refus ou arrêt : on retente sauf arrêt voulu
      }
      if (ctrl.signal.aborted) return
      opts.onDown?.()
      await new Promise((r) => setTimeout(r, delay))
      delay = Math.min(delay * 2, 30_000)
    }
  }
  void loop()
  return () => ctrl.abort()
}
